import { PlusOutlined } from '@ant-design/icons';
import { Alert, Button, Checkbox, Col, Divider, Form, Input, Modal, Row, Select, Spin, Typography } from 'antd';
import { RuleObject } from 'antd/es/form';
import { groupBy, isEqual, parseInt } from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ENV } from 'src';
import { AppConfiguration } from 'src/core/services/configuration.service';
import { CommandResult } from 'src/core/stores/types';
import { curateString } from 'src/core/utils/object';
import { AppConfig, container } from 'src/inversify.config';
import { PermissionSummaryStore } from 'src/stores/permissions-store';
import { TutorialFunctionItemStore, TutorialFunctionSummaryStore, TutorialItemStore, TutorialSummary } from 'src/stores/video-tutorials-store';

const { Text } = Typography;

export type TutorialDetailsEditProps = { item: TutorialSummary; mode: 'edit' };
export type TutorialDetailsNewProps = { item: undefined; mode: 'new' };

export type TutorialDetailsProps = { onClose: (response?: CommandResult<TutorialSummary>) => void } & (
  | TutorialDetailsNewProps
  | TutorialDetailsEditProps
);

export const TutorialDetails = (props: TutorialDetailsProps) => {
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();
  const { onClose } = props;
  const [form] = Form.useForm<{
    permissions: string[];
    functionId?: string;
    functionOrder?: string;
    isEnabled: boolean;
    synchronizeThisVideo: boolean;
    universalId: string;
  }>();

  const tutorialItemStore = container.get(TutorialItemStore);
  const tutorialItemState = tutorialItemStore.state;
  const tutorialFunctionItemStore = container.get(TutorialFunctionItemStore);
  const tutorialFunctionStore = container.get(TutorialFunctionSummaryStore);
  const tutorialFunctionState = tutorialFunctionStore.state;

  const permissionsStore = container.get(PermissionSummaryStore);
  const permissionsState = permissionsStore.state;

  const [submittable, setSubmittable] = useState(true);
  const values = Form.useWatch([], form);
  const [newFunctionName, setNewFunctionName] = useState<string>('');
  const [functionSelectVisible, setFunctionSelectVisible] = useState(false);
  const [functionOptions, setFunctionOptions] = useState<{ id: string; title: string; existingOrders: number[] }[]>([]);
  const [functionAlreadyExists, setFunctionAlreadyExists] = useState(false);
  const [error, setError] = useState<string>();

  const config: AppConfiguration = useContext(AppConfig);

  useEffect(() => {
    form
      .validateFields()
      .then(() => {
        setSubmittable(true);
      })
      .catch((error) => {
        if (!error?.errorFields?.length && error?.outOfDate) {
          form.validateFields();
        } else setSubmittable(false);
      });
  }, [form, values]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await tutorialFunctionStore.load({ take: 100000, skip: 0 });
      await permissionsStore.load({ take: 100000, skip: 0 });
      const groupedOptions = Object.entries(groupBy(tutorialFunctionState.items.value, 'functionTitle')).map(([key, value]) => {
        return {
          title: key,
          id: value.firstOrDefault()?.id ?? '',
          existingOrders: value.map((v) => v.order),
        };
      });

      setFunctionOptions(groupedOptions);

      setLoading(false);
      //load function stuff
    };
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onEditTutorial = async () => {
    let item = await form.validateFields();
    if (item.functionId && item.functionOrder) {
      const selectedFunctionTitle = functionOptions.find((v) => v.id === item.functionId)?.title!;
      const functionWithOrder = tutorialFunctionState.items.value.find(
        (v) => v.functionTitle === selectedFunctionTitle && v.order === parseInt(item.functionOrder!)
      );
      if (functionWithOrder) {
        item.functionId = functionWithOrder.id;
      } else {
        const result = await tutorialFunctionItemStore.create({ functionTitle: selectedFunctionTitle, order: parseInt(item.functionOrder) });
        item.functionId = result?.identity;
      }
    }

    const payload: Partial<TutorialSummary> = {
      ...props.item,
      permissions: undefined,
      functionTitle: undefined,
      functionId: item.functionId,
      isEnabled: item?.isEnabled,
      universalId: item.universalId,
    };

    const response = props.mode === 'edit' ? await tutorialItemStore.save(props.item.id, payload) : await tutorialItemStore.create(payload);
    if (response?.isSuccess && response.identity) {
      let responseError: string | undefined = undefined;
      if (
        !isEqual(
          props?.item?.permissions.map((v) => v.permissionId),
          values?.permissions
        ) ||
        props.mode === 'new'
      ) {
        const permissionsResponse = await tutorialItemStore.updatePermissions(response.identity, item.permissions);
        responseError = permissionsResponse?.error;
      }
      if (props.mode === 'edit' && item.synchronizeThisVideo) {
        const synchronizationResponse = await tutorialItemStore.synchronizeVideo(response.identity);
        responseError = synchronizationResponse?.error;
      }
      if (!responseError) {
        onClose(response);
      } else {
        setError(responseError);
      }
    } else if (response?.error) setError(response.error);
  };

  const getInitialValues = () => {
    if (props.mode === 'new') return {};
    const perms = props.item.permissions.map((v) => v.permissionId);
    if (props.item.functionId) {
      const itemFunction = tutorialFunctionState.items.value.find((v) => v.id === props.item.functionId);
      const groupedFunction = functionOptions.find((v) => v.title === itemFunction?.functionTitle);
      return {
        permissions: perms,
        functionOrder: `${itemFunction?.order}`,
        functionId: groupedFunction?.id,
        isEnabled: props.item?.isEnabled,
        universalId: props.item?.universalId,
      };
    }
    return { permissions: perms, isEnabled: props.item?.isEnabled, universalId: props.item?.universalId };
  };

  const addFunction = () => {
    const creatingFunctionOrder = values.functionOrder ? parseInt(values.functionOrder) : 1;
    const creatingFunctionName = newFunctionName;
    tutorialFunctionItemStore.create({ functionTitle: creatingFunctionName, order: creatingFunctionOrder }).then((result) => {
      if (result?.isSuccess) {
        setFunctionOptions((prev) => [...prev, { id: result?.identity!, existingOrders: [creatingFunctionOrder], title: creatingFunctionName }]);
        form.setFieldValue('functionId', result?.identity!);
        tutorialFunctionState.set((s) => {
          s.items = [...s.items, { functionTitle: creatingFunctionName, id: result.identity!, order: creatingFunctionOrder }];
          s.count = s.count + 1;
          return s;
        });
      }
    });
    setNewFunctionName('');
    setFunctionSelectVisible(false);
  };

  const isUnchanged = () => {
    if (props.mode === 'new') {
      return values?.universalId === undefined;
    }
    if (values?.isEnabled !== props.item?.isEnabled) return false;

    if (
      !isEqual(
        props?.item?.permissions.map((v) => v.permissionId),
        values?.permissions
      )
    ) {
      return false;
    }

    const selectedFunctionTitle = functionOptions.find((v) => v.id === values.functionId)?.title;
    const functionWithOrder = tutorialFunctionState.items.value.find(
      (v) => v.functionTitle === selectedFunctionTitle && v.order === parseInt(values.functionOrder ?? '')
    );

    if ((functionWithOrder?.id !== props.item.functionId && props.item.functionId) || (!props.item.functionId && values.functionId)) return false;

    if (props.item.universalId !== values.universalId) return false;

    return true;
  };

  return (
    <Modal
      maskClosable={false}
      open
      okButtonProps={{ disabled: !submittable || isUnchanged() }}
      width={600}
      onCancel={() => onClose()}
      title={t(props.mode === 'edit' ? 'EditTutorialInformation' : 'AddTutorialInformation')}
      onOk={onEditTutorial}
    >
      <Spin spinning={loading || tutorialFunctionState.isBusy.get() || tutorialItemState.isBusy.get() || permissionsState.isBusy.get()}>
        {!loading && (
          <Form form={form} layout="vertical" initialValues={getInitialValues()}>
            {error !== undefined && (
              <Alert
                type="error"
                message={`${t(`Error${props.mode === 'edit' ? 'Editing' : 'Creating'}Video`)}${config.environment === ENV.DEV ? `: ${error}` : ''}`}
              />
            )}
            <Form.Item name={'permissions'} label={t('Permissions')}>
              <Select
                optionFilterProp="label"
                mode="multiple"
                placeholder={t('Permissions')}
                allowClear
                options={permissionsState.items.value.map((perm) => ({ label: t(perm.name), key: perm.id, value: perm.id }))}
                style={{ width: '100%' }}
              />
            </Form.Item>
            <Row style={{ width: '100%' }} gutter={10}>
              <Col span={16}>
                <Form.Item name={'functionId'} label={t('FuntionTitle')}>
                  <Select
                    placeholder={t('VideoFunction')}
                    allowClear
                    open={functionSelectVisible}
                    onOpenChange={(open) => setFunctionSelectVisible(open)}
                    options={functionOptions.map((v) => ({ label: v.title, value: v.id, key: v.id }))}
                    popupRender={(menu) => (
                      <>
                        {menu}
                        <Divider style={{ margin: '8px 0' }} />
                        <div style={{ width: '100%', margin: 8 }}>
                          <Input
                            style={{ width: '85%' }}
                            placeholder={t('AddFunction')}
                            value={newFunctionName}
                            onChange={(v) => {
                              setNewFunctionName(v.target.value);
                              setFunctionAlreadyExists(functionOptions.some((func) => curateString(func.title) === curateString(v.target.value)));
                            }}
                          />
                          <Button
                            disabled={functionAlreadyExists || !newFunctionName}
                            type="text"
                            icon={<PlusOutlined />}
                            style={{ marginLeft: 4 }}
                            onClick={addFunction}
                          />
                        </div>
                        {functionAlreadyExists && <Text type="danger">{t('FunctionAlreadyExistsWarning')}</Text>}
                      </>
                    )}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name={'functionOrder'}
                  label={t('Order')}
                  dependencies={['functionId']}
                  rules={[
                    (formInstance) => ({
                      validator: (ruleObj: RuleObject, value: string | undefined) => {
                        const functionId = formInstance.getFieldValue('functionId');
                        if (!value && functionId) {
                          return Promise.reject(t('PleaseEnterVideoOrder'));
                        }
                        return Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <Input
                    disabled={!values.functionId}
                    autoComplete="off"
                    allowClear
                    placeholder={t('VideoOrder')}
                    onChange={(e) => {
                      const regex = /[^0-9]/g;
                      form.setFieldsValue({ functionOrder: e.target.value.replace(regex, '') });
                    }}
                  />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item name={'isEnabled'} valuePropName="checked">
                  <Checkbox>{t('TutorialEnabledLabel')}</Checkbox>
                </Form.Item>
              </Col>

              <Col span={24}>
                <Form.Item name={'universalId'} label={t('VideoId')} rules={[{ required: true }]}>
                  <Input autoComplete="off" allowClear placeholder={t('VideoId')} />
                </Form.Item>
              </Col>

              {props.mode === 'edit' && (
                <Col span={24}>
                  <Form.Item name={'synchronizeThisVideo'} valuePropName="checked">
                    <Checkbox>{t('SynchronizeThisVideoAfterOperationFinishes')}</Checkbox>
                  </Form.Item>
                </Col>
              )}
            </Row>
          </Form>
        )}
      </Spin>
    </Modal>
  );
};
